import { forwardRef, memo, useCallback, useImperativeHandle, useMemo, useState } from 'react';

import { type Color } from '@air/api/types';
import { CustomColorPickerInput } from '@air/component-custom-color-picker-input';
import { Close } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';

import { type ColorToggleParams } from '~/components/Filters/ColorFilter/ColorFilterCard';
import { CustomColorPickerToggle } from '~/components/Filters/ColorFilter/components/CustomColorPickerToggle';
import { CustomColorValue } from '~/components/Filters/ColorFilter/components/CustomColorValue';
import { useCustomColorPickerStore } from '~/components/v2/store/features/filters/useCustomColorPickerStore';

export type CustomColorPickerControlProps = {
  colors: Color[];
  onColorSelect: (params: ColorToggleParams) => void;
  onRemoveHex: (hex: string) => void;
  selectedColors: string[];
};

export type CustomColorPickerControlRef = {
  clearSavedHexes: () => void;
};

export const CustomColorPickerControl = memo(
  forwardRef<CustomColorPickerControlRef, CustomColorPickerControlProps>(
    ({ colors, onColorSelect, onRemoveHex, selectedColors }, ref) => {
      const addHex = useCustomColorPickerStore((state) => state.addHex);
      const removeHex = useCustomColorPickerStore((state) => state.removeHex);
      const hexes = useCustomColorPickerStore((state) => state.hexes);

      const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

      // Expose method to clear saved hexes
      useImperativeHandle(ref, () => ({
        clearSavedHexes: () => {
          // Instead of removing the chips, just deselect them in Redux
          hexes.forEach((hex) => {
            if (selectedColors.includes(hex)) {
              onColorSelect({ color: { hex, name: hex }, isSelected: false });
            }
          });
        },
      }));

      const mergedHexes = useMemo(() => {
        // Filter out the default colors from the selected colors.
        const filteredSelectedColors = selectedColors.filter((color) => !colors.some((c) => c.hex === color));

        return [...new Set([...hexes, ...filteredSelectedColors])];
      }, [colors, hexes, selectedColors]);

      const handleRemoveHex = useCallback(
        (hexToRemove: string) => {
          removeHex(hexToRemove);
          onColorSelect({ color: { hex: hexToRemove, name: hexToRemove }, isSelected: false });
          onRemoveHex(hexToRemove);
        },
        [onColorSelect, onRemoveHex, removeHex],
      );

      const onSaveCustomColor = useCallback(
        (hex: string) => {
          // Only add the hex if it doesn't already exist in savedHexes
          if (!hexes.includes(hex)) {
            addHex(hex);
          }
          // Always select the color, even if it was already saved but not selected
          onColorSelect({ color: { hex, name: hex }, isSelected: true });
          setIsColorPickerOpen(false);
        },
        [addHex, onColorSelect, hexes],
      );

      const onToggleColorPicker = useCallback(() => {
        setIsColorPickerOpen((prev) => !prev);
      }, []);

      const onCustomColorSelect = useCallback(
        ({ hex, isSelected }: { hex: string; isSelected: boolean }) => {
          if (!hexes.includes(hex)) {
            addHex(hex);
          }
          onColorSelect({ color: { hex, name: hex }, isSelected });
        },
        [addHex, onColorSelect, hexes],
      );

      return (
        <div data-testid="CUSTOM_COLOR_PICKER_CONTROL">
          <div className="flex flex-col items-start gap-3">
            <p className="font-medium text-12 text-grey-12">More colors</p>

            <div className="flex w-full flex-col gap-2">
              <div className="flex flex-wrap gap-2">
                <CustomColorPickerToggle data-selected={isColorPickerOpen} onClick={onToggleColorPicker} />

                {mergedHexes.length > 0 &&
                  mergedHexes.map((hex) => {
                    const isSelected = selectedColors.includes(hex);

                    return (
                      <CustomColorValue
                        hex={hex}
                        isSelected={isSelected}
                        onSelect={() => onCustomColorSelect({ hex, isSelected: !isSelected })}
                        onRemoveHex={() => handleRemoveHex(hex)}
                        key={hex}
                      />
                    );
                  })}
              </div>
            </div>

            {isColorPickerOpen && (
              <div className="relative flex w-full flex-col items-start gap-3 rounded-8 border border-grey-5 px-4 py-3 pb-4">
                <IconButton
                  icon={Close}
                  label="Close"
                  appearance="ghost"
                  color="grey"
                  className="absolute top-4 right-4 size-3"
                  onClick={() => setIsColorPickerOpen(false)}
                />
                <p className="font-medium text-12 text-grey-12" data-testid="CUSTOM_COLOR_PICKER_LABEL">
                  Custom color
                </p>
                <CustomColorPickerInput onSave={onSaveCustomColor} />
              </div>
            )}
          </div>
        </div>
      );
    },
  ),
);

CustomColorPickerControl.displayName = 'CustomColorPickerControl';
